import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  BarChart3,
  TrendingUp,
  FolderOpen,
  Ticket,
} from "lucide-react";
import Loader from "@/components/Loader";
import { toast } from "sonner";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";

import {
  getAuth,
  signOut
} from "firebase/auth";

import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getCountFromServer,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
} from "firebase/firestore";
import { useNavigate, useNavigation } from "react-router-dom";

export default function Dashboard() {
  const auth = getAuth();
  const db = getFirestore();

  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [recentLogs, setRecentLogs] = useState([]);
  const [topProjects, setTopProjects] = useState([]);
  const [dueTickets, setDueTickets] = useState([]);
  const [charts, setCharts] = useState({
    statusData: [],
    dailyData: [],
  });

  /* ---------------- USER + STATS ---------------- */

const navigate = useNavigate();

useEffect(() => {
  const loadUser = async () => {
    const firebaseUser = auth.currentUser;
debugger;
    // 🔐 Hard guard (extra safety)
    if (!firebaseUser) {
      await signOut(auth);
      navigate("/projects", { replace: true });
      return;
    }

    const snap = await getDoc(doc(db, "users", firebaseUser.uid));

    // ❌ User deleted / invalid
    if (!snap.exists()) {
      await signOut(auth);
      toast.error("Session expired. Please login again.");
      navigate("/login", { replace: true });
      return;
    }

    const userData = snap.data();

    // ❌ Invalid role
    if (!userData?.role) {
      await signOut(auth);
      navigate("/login", { replace: true });
      return;
    }

    // ✅ Valid user
    setUser({ ...userData, id: firebaseUser.uid });

    await fetchStats(firebaseUser.uid, userData.role);
    await fetchGraphData();
  };

  loadUser();
}, [navigate]);



  // useEffect(() => {
  //   const loadUser = async () => {
  //     const firebaseUser = auth.currentUser;
  //     if (!firebaseUser) return;

  //     const snap = await getDoc(doc(db, "users", firebaseUser.uid));
  //     if (!snap.exists()) return;

  //     const userData = snap.data();
  //     setUser({ ...userData, id: firebaseUser.uid });

  //     await fetchStats(firebaseUser.uid, userData.role);
  //     await fetchGraphData();
  //   };

  //   loadUser();
  // }, []);

  /* ---------------- STATS ---------------- */
  const fetchStats = async (uid, role) => {
    try {
      const s = {};

      if (role === "super_admin") {
        s.total_admins = (
          await getCountFromServer(
            query(collection(db, "users"), where("role", "==", "admin"))
          )
        ).data().count;

        s.total_projects = (
          await getCountFromServer(collection(db, "projects"))
        ).data().count;

        s.total_tickets = (
          await getCountFromServer(collection(db, "tickets"))
        ).data().count;

        s.open_tickets = (
          await getCountFromServer(
            query(collection(db, "tickets"), where("status", "==", "Open"))
          )
        ).data().count;
      }

      setStats(s);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load dashboard stats");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- RECENT ACTIVITY ---------------- */
  useEffect(() => {
    const q = query(
      collection(db, "logs"),
      orderBy("created_at", "desc"),
      limit(8)
    );

    const unsub = onSnapshot(q, (snap) => {
      setRecentLogs(
        snap.docs.map((d) => ({ id: d.id, ...d.data() }))
      );
    });

    return () => unsub();
  }, []);

  /* ---------------- GRAPHS ---------------- */
  const fetchGraphData = async () => {
    /* ---------- STATUS GRAPH ---------- */
    const statusCount = {
      Open: 0,
      "In Progress": 0,
      Resolved: 0,
      Rejected: 0,
    };

    const ticketsSnap = await getDocs(collection(db, "tickets"));

    ticketsSnap.forEach((doc) => {
      const s = doc.data().status;
      if (statusCount[s] !== undefined) statusCount[s]++;
    });

    const statusData = Object.keys(statusCount).map((k) => ({
      name: k,
      value: statusCount[k],
    }));

    /* ---------- DAILY GRAPH ---------- */
    const today = new Date();
    const dailyMap = {};

    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(today.getDate() - i);
      dailyMap[d.toISOString().slice(0, 10)] = 0;
    }

    ticketsSnap.forEach((doc) => {
      const t = doc.data();
      if (!t.created_at) return;

      const day = t.created_at.toDate().toISOString().slice(0, 10);
      if (dailyMap[day] !== undefined) dailyMap[day]++;
    });

    const dailyData = Object.keys(dailyMap).map((d) => ({
      date: d,
      tickets: dailyMap[d],
    }));

    setCharts({ statusData, dailyData });

    /* ---------- TOP PROJECTS (FIXED) ---------- */

    const projectSnap = await getDocs(collection(db, "projects"));
    const projects = projectSnap.docs.map((d) => ({
      id: d.id,
      name: d.data().name,
    }));

    const projectMap = {};

    // ✅ COUNT TICKETS PER PROJECT FIRST
    ticketsSnap.forEach((doc) => {
      const pid = doc.data().project_id;
      if (!pid) return;
      projectMap[pid] = (projectMap[pid] || 0) + 1;
    });

    // ✅ SORT AFTER COUNTING
    const sorted = Object.entries(projectMap)
      .sort((a, b) => b[1] - a[1])

    const max = sorted[0]?.[1] || 1;

    setTopProjects(
      sorted.map(([pid, count]) => ({
        project_id: pid,
        name: projects.find((p) => p.id === pid)?.name || "Unknown",
        count,
        percent: Math.round((count / max) * 100),
      }))
    );

    /* ---------- DUE TICKETS ---------- */
    const now = new Date();
    const overdue = [];

    ticketsSnap.forEach((doc) => {
      const t = doc.data();
      if (!t.due_date) return;

      const dueDate = new Date(t.due_date);
      if (dueDate < now && t.status !== "Resolved" && t.status !== "Rejected") {
        const projectName = projects.find((p) => p.id === t.project_id)?.name || "Unknown";
        const daysOverdue = Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));

        overdue.push({
          id: doc.id,
          title: t.title,
          project: projectName,
          daysOverdue,
          dueDate: t.due_date,
          dueDateTimestamp: dueDate.getTime(),
        });
      }
    });

    // Sort by oldest due date first (earliest overdue tickets first)
    overdue.sort((a, b) => a.dueDateTimestamp - b.dueDateTimestamp);

    // Get max days overdue for percentage calculation
    const maxDays = overdue[0]?.daysOverdue || 1;

    setDueTickets(
      overdue.slice(0, 10).map((ticket) => ({
        ...ticket,
        percent: Math.round((ticket.daysOverdue / maxDays) * 100),
      }))
    );
  };


  /* ---------------- FORMAT TIMESTAMP ---------------- */
  const formatTimestamp = (timestamp) => {
    if (timestamp?.toDate) {
      return timestamp.toDate().toLocaleDateString();
    }
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toLocaleDateString();
    }
    return 'N/A';
  };

  /* ---------------- STAT CARDS ---------------- */
  const statCards = [
    {
      title: "Total Admins",
      value: stats.total_admins || 0,
      icon: BarChart3,
      color: "bg-blue-500",
    },
    {
      title: "Total Projects",
      value: stats.total_projects || 0,
      icon: FolderOpen,
      color: "bg-green-500",
    },
    {
      title: "Total Tickets",
      value: stats.total_tickets || 0,
      icon: Ticket,
      color: "bg-purple-500",
    },
    {
      title: "Open Tickets",
      value: stats.open_tickets || 0,
      icon: TrendingUp,
      color: "bg-orange-500",
    },
  ];

  if (!user) return <Loader />;

  return (
    <Layout user={user}>
      <h1 className="text-4xl font-bold mb-2">Dashboard</h1>
      <p className="text-gray-600 mb-8">
        Welcome back, {user.name}
      </p>

      {loading ? (
        <Loader />
      ) : (
        <>
          {/* ---------------- STATS GRID ---------------- */}
{user?.role === "super_admin"  &&          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((c, i) => {
              const Icon = c.icon;
              return (
                <Card key={i}>
                  <CardHeader className="flex justify-between">
                    <CardTitle className="text-sm">{c.title}</CardTitle>
                    <div className={`p-2 rounded ${c.color}`}>
                      <Icon className="text-white h-4 w-4" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold">{c.value}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>}
          {user?.role === "admin" || user?.role === "super_admin" && (
            <>
              <div className="flex gap-6 mt-6">
                {/* TOP PROJECTS */}
                <div style={{ width: '49%' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm text-gray-600">
                        Top Projects
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {topProjects.length > 0 ? (
                        topProjects.map(p => (
                          <div key={p.project_id}>
                            <div className="flex justify-between text-sm">
                              <span>{p.name}</span>
                              <span>{p.count}</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded">
                              <div
                                className="h-2 bg-blue-600 rounded"
                                style={{ width: `${p.percent}%` }}
                              />
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No projects found</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* DUE TICKETS */}
                <div style={{ width: '49%' }}>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm text-gray-600">
                        Due Tickets
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {dueTickets.length > 0 ? (
                        dueTickets.map(ticket => (
                          <div key={ticket.id}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-gray-600">{ticket.project}</span>
                              <span className="text-red-600 font-medium">
                                {formatTimestamp(ticket.dueDate)}
                              </span>
                            </div>
                            <div className="text-sm font-medium truncate mb-1">
                              {ticket.title}
                            </div>
                            <div className="h-2 bg-gray-200 rounded">
                              <div
                                className="h-2 bg-red-600 rounded"
                                style={{ width: `${ticket.percent}%` }}
                              />
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No overdue tickets</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
                <Card className="h-[420px]">
                  <CardHeader>
                    <CardTitle>Tickets by Status</CardTitle>
                  </CardHeader>
                  <CardContent className="h-[340px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={charts.statusData}>
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar
                          dataKey="value"
                          fill="#6366f1"
                          radius={[6, 6, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="h-[420px]">
                  <CardHeader>
                    <CardTitle>
                      Tickets Created (Last 7 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="h-[340px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={charts.dailyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="tickets"
                          stroke="#22c55e"
                          strokeWidth={3}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </>
          )}

        </>
      )}
    </Layout>
  );
}
function Stat({ title, value, icon: Icon }) {
  return (
    <Card>
      <CardHeader className="flex justify-between">
        <CardTitle className="text-sm text-gray-600">{title}</CardTitle>
        <Icon className="h-4 w-4 text-gray-500" />
      </CardHeader>
      <CardContent>
        <p className="text-3xl font-bold">{value}</p>
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, children }) {
  return (
    <Card className="h-[360px]">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}